import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Award, Users, Globe, Heart, Sparkles, Target, Compass, ShieldCheck,
  Rocket, Cpu, Building2, HeartPulse, Code2, ArrowRight, Quote, Trophy,
  GraduationCap, Stethoscope, Cog, Lightbulb,
} from 'lucide-react';
import { useSiteI18n } from '../SiteI18n';
import { SiteSEO } from '../SiteSEO';
import { SiteCtaBlock } from '../components/SitePageParts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import heroImg from '../assets/hero-aetheris.jpg';

const SiteDespre: React.FC = () => {
  const { lang } = useSiteI18n();

  const ro = lang === 'ro';

  const principles = ro ? [
    { icon: Heart, t: 'Misiune', d: 'Construim infrastructura digitală a Europei: orașe inteligente, clădiri responsabile, sănătate de spital în buzunar.' },
    { icon: Compass, t: 'Viziune', d: 'AETHERIS să devină standardul tehnologic al organizațiilor publice și private în următorul deceniu.' },
    { icon: Users, t: 'Echipă', d: 'Ingineri software, medici, urbaniști, criptografi și specialiști AI uniți într-un singur produs.' },
    { icon: Award, t: 'Standarde', d: 'Etică, transparență, securitate clinică și conformitate UE — fără compromisuri, niciodată.' },
  ] : [
    { icon: Heart, t: 'Mission', d: 'We build the digital infrastructure of Europe: smart cities, responsible buildings, hospital-grade health in the pocket.' },
    { icon: Compass, t: 'Vision', d: 'AETHERIS to become the tech standard for public and private organisations in the next decade.' },
    { icon: Users, t: 'Team', d: 'Software engineers, doctors, urbanists, cryptographers and AI specialists united around one product.' },
    { icon: Award, t: 'Standards', d: 'Ethics, transparency, clinical-grade security and EU compliance — never compromised.' },
  ];

  const numbers = ro ? [
    { v: '7', l: 'module verticale integrate' },
    { v: '320k+', l: 'cetățeni serviți zilnic' },
    { v: '60+', l: 'edge functions hardened' },
    { v: '12+', l: 'specialități clinice live' },
    { v: '99.98%', l: 'uptime garantat' },
    { v: 'EU', l: 'data residency strictă' },
  ] : [
    { v: '7', l: 'integrated vertical modules' },
    { v: '320k+', l: 'citizens served daily' },
    { v: '60+', l: 'hardened edge functions' },
    { v: '12+', l: 'clinical specialties live' },
    { v: '99.98%', l: 'guaranteed uptime' },
    { v: 'EU', l: 'strict data residency' },
  ];

  const story = ro ? [
    { y: '2022', t: 'Punctul zero', d: 'O echipă mică din Timișoara realizează că orașele, spitalele și clădirile rulează pe sisteme deconectate, scumpe și opace. Începe AETHERIS.' },
    { y: '2023', t: 'Orașul, online.', d: 'Smart City intră în producție: trafic AI, parcări, mobilitate — totul într-o singură platformă.' },
    { y: '2024', t: 'KNX', d: 'Building & KNX, control real al clădirii din browser la un click.' },
    { y: '2025', t: 'Cybersecurity & ERP', d: 'Adăugăm Secure (NIS2 dashboard live), Intelligence (AI Gateway) și Finance (facturare + parsere AI).' },
    { y: '2026', t: 'Sănătate', d: 'Lansăm AETHERIS Care (consultații E2E, Medic AI). „Fii mai sănătos pentru a reuși în tot ce faci!"' },
    { y: '2026', t: 'Operating System complet', d: 'Cele 7 module devin un singur OS modular, multi-tenant, federabil — gata pentru organizații europene de orice scală.' },
  ] : [
    { y: '2022', t: 'Ground zero', d: 'A small Timișoara team realises that cities, hospitals and buildings run on disconnected, expensive, opaque systems. AETHERIS begins.' },
    { y: '2023', t: 'The city, online.', d: 'Smart City goes into production: AI traffic, parking, mobility — all in one platform.' },
    { y: '2024', t: 'KNX', d: 'Building & KNX, real building control from the browser, one click away.' },
    { y: '2025', t: 'Cybersecurity & ERP', d: 'We add Secure (live NIS2 dashboard), Intelligence (AI Gateway) and Finance (invoicing + AI parsers).' },
    { y: '2026', t: 'Health', d: 'We launch AETHERIS Care (E2E consultations, Medic AI). "Be healthier to succeed in everything you do!"' },
    { y: '2026', t: 'Full operating system', d: 'The 7 modules become a single modular, multi-tenant, federable OS — ready for European organisations at any scale.' },
  ];

  const expertise = ro ? [
    { icon: Cpu, t: 'Inginerie de produs', d: 'Code review obligatoriu, type-safety strictă, testare automată pe fluxurile critice și deploy-uri continue. Zero compromisuri pe cod care atinge date clinice sau financiare.' },
    { icon: Stethoscope, t: 'Cunoaștere clinică', d: 'Lucrăm direct cu medici români din 33+ specialități pentru a valida fluxurile, scalele și documentele oficiale.' },
    { icon: Building2, t: 'Inginerie KNX/BMS', d: 'Implementări reale în spitale, hoteluri și complexe rezidențiale active. KNX/IP, Modbus, BACnet.' },
    { icon: ShieldCheck, t: 'Securitate cibernetică', d: 'Threat modeling, RLS, MFA, WebAuthn, runtime pentest, NIS2/DORA — by design, nu by patch.' },
    { icon: Globe, t: 'Conformitate UE', d: 'GDPR, NIS2, DSA, AI Act. Consultanță juridică internă, DPIA pentru fiecare modul nou.' },
    { icon: Code2, t: 'Open standards', d: 'Folosim și contribuim la standarde deschise: KNX, OCPP, FHIR, OpenAPI, RFC 8291, WebAuthn.' },
  ] : [
    { icon: Cpu, t: 'Product engineering', d: 'Mandatory code review, strict type-safety, automated tests on critical flows and continuous deployments. Zero compromise on code that touches clinical or financial data.' },
    { icon: Stethoscope, t: 'Clinical expertise', d: 'We work directly with doctors across 33+ specialties to validate flows, scales and official documents.' },
    { icon: Building2, t: 'KNX/BMS engineering', d: 'Real deployments in active hospitals, hotels and residential complexes. KNX/IP, Modbus, BACnet.' },
    { icon: ShieldCheck, t: 'Cybersecurity', d: 'Threat modeling, RLS, MFA, WebAuthn, runtime pentest, NIS2/DORA — by design, not by patch.' },
    { icon: Globe, t: 'EU compliance', d: 'GDPR, NIS2, DSA, AI Act. In-house legal counsel, DPIA for every new module.' },
    { icon: Code2, t: 'Open standards', d: 'We use and contribute to open standards: KNX, OCPP, FHIR, OpenAPI, RFC 8291, WebAuthn.' },
  ];

  const principlesEng = ro ? [
    'Securitate înainte de viteză — niciodată invers.',
    'Codul fără teste de securitate nu ajunge în producție.',
    'Datele clinice și ale cetățeanului nu se vând. Vreodată.',
    'Fiecare flux are un audit trail complet, fără excepție.',
    'AI-ul sugerează. Omul decide. Întotdeauna.',
    'Open standards > vendor lock-in. Mereu.',
  ] : [
    'Security before speed — never the other way around.',
    'Code without security tests never reaches production.',
    'Clinical and citizen data is never sold. Ever.',
    'Every flow has a full audit trail, no exceptions.',
    'AI suggests. Humans decide. Always.',
    'Open standards > vendor lock-in. Always.',
  ];

  const recognition = ro ? [
    { icon: Trophy, t: 'Conformitate UE', d: 'GDPR · NIS2 · DSA · AI Act' },
    { icon: GraduationCap, t: 'Parteneriate academice', d: 'Universitatea de Medicină și institute tehnice' },
    { icon: Cog, t: 'Producție 24/7', d: 'Spitale, primării și clădiri rezidențiale active' },
    { icon: Lightbulb, t: 'R&D continuu', d: '> 30% din capacitate dedicată cercetării' },
  ] : [
    { icon: Trophy, t: 'EU compliance', d: 'GDPR · NIS2 · DSA · AI Act' },
    { icon: GraduationCap, t: 'Academic partnerships', d: 'Medical university & technical institutes' },
    { icon: Cog, t: '24/7 production', d: 'Active hospitals, city halls and residential buildings' },
    { icon: Lightbulb, t: 'Continuous R&D', d: '> 30% of capacity invested in research' },
  ];

  return (
    <>
      <SiteSEO
        path="/site/despre"
        title={ro ? 'Despre AETHERIS — Misiune, viziune, echipă, principii' : 'About AETHERIS — Mission, vision, team, principles'}
        description={ro
          ? 'AETHERIS construiește infrastructura digitală a Europei pentru orașe, clădiri și sănătate. Echipă de ingineri, medici, urbaniști. Etică, securitate clinică, conformitate UE.'
          : 'AETHERIS builds the digital infrastructure of Europe for cities, buildings and health. A team of engineers, doctors and urbanists. Ethics, clinical-grade security, EU compliance.'}
      />

      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={heroImg} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-gradient-to-br from-primary/30 via-violet-500/15 to-cyan-500/20 rounded-full blur-[140px] site-aurora" />
          <div className="absolute inset-0 site-grid-bg" />
        </div>
        <div className="container mx-auto px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge className="mb-5 bg-primary/10 text-primary border border-primary/30 backdrop-blur-md">
              <Sparkles className="h-3 w-3 mr-1.5" /> {ro ? 'Despre noi' : 'About us'}
            </Badge>
            <h1 className="text-4xl md:text-7xl font-black tracking-tight leading-[0.98] mb-5">
              <span className="bg-gradient-to-br from-foreground via-foreground to-foreground/40 bg-clip-text text-transparent block">
                {ro ? 'Construim AETHERIS' : 'We build AETHERIS'}
              </span>
              <span className="bg-gradient-to-br from-primary via-primary to-cyan-400 bg-clip-text text-transparent block">
                {ro ? 'pentru a schimba Europa.' : 'to change Europe.'}
              </span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {ro
                ? 'Sediul în Timișoara, ambiția în întreaga Uniune Europeană. O echipă care nu acceptă status-quo-ul digital al instituțiilor publice și sistemului de sănătate.'
                : 'Headquartered in Timișoara, ambition across the European Union. A team that refuses the digital status quo of public institutions and healthcare.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="py-12">
        <div className="container mx-auto px-4 lg:px-6 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {principles.map((v, i) => (
            <motion.div
              key={v.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              whileHover={{ y: -4 }}
              className="group relative rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-6 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-primary/15 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity" />
              <div className="relative">
                <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center text-primary mb-4">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-lg mb-2">{v.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BY THE NUMBERS */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 site-grid-bg opacity-30" />
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {ro ? 'În cifre' : 'By the numbers'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
              {ro ? 'Realitatea, nu marketing.' : 'Reality, not marketing.'}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {numbers.map((n, i) => (
              <motion.div
                key={n.l}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-5 text-center hover:border-primary/40 transition-all"
              >
                <div className="text-2xl md:text-3xl font-black bg-gradient-to-br from-primary to-cyan-400 bg-clip-text text-transparent">
                  {n.v}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{n.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE / STORY */}
      <section className="py-20 bg-card/30 border-y border-border/60">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {ro ? 'Povestea noastră' : 'Our story'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {ro ? 'Cum am ajuns aici.' : 'How we got here.'}
            </h2>
            <p className="text-muted-foreground">
              {ro
                ? 'De la o frustrare comună până la un Operating System modular folosit zilnic de zeci de mii de oameni.'
                : 'From a shared frustration to a modular Operating System used daily by tens of thousands of people.'}
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent" />
            <div className="space-y-8">
              {story.map((s, i) => {
                const right = i % 2 === 1;
                return (
                  <motion.div
                    key={s.y}
                    initial={{ opacity: 0, x: right ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-8 items-center ${right ? '' : ''}`}
                  >
                    <div className={`${right ? 'md:order-2' : ''}`}>
                      <div className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md p-5 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-primary/15 text-primary border border-primary/30">{s.y}</Badge>
                          <h3 className="font-bold text-lg">{s.t}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                      </div>
                    </div>
                    <div className={`hidden md:block ${right ? 'md:order-1' : ''}`} />
                    <div className="absolute left-2 md:left-1/2 top-5 -translate-x-1/2">
                      <div className="h-4 w-4 rounded-full bg-primary shadow-[0_0_0_4px] shadow-primary/30" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* EXPERTISE */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {ro ? 'Expertiză' : 'Expertise'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {ro ? 'Șase discipline. Un singur produs.' : 'Six disciplines. One product.'}
            </h2>
            <p className="text-muted-foreground">
              {ro
                ? 'AETHERIS nu este construit de o singură funcție. Este rezultatul unei colaborări strânse între ingineri, medici, juriști și operatori reali.'
                : 'AETHERIS is not built by a single function. It is the result of a tight collaboration between engineers, doctors, lawyers and real operators.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {expertise.map((e, i) => (
              <motion.div
                key={e.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className="group relative rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-6 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-primary/15 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity" />
                <div className="relative">
                  <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center mb-3">
                    <e.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-base mb-1.5">{e.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{e.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO / PRINCIPLES */}
      <section className="py-20 bg-card/30 border-y border-border/60">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {ro ? 'Manifest' : 'Manifesto'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {ro ? 'Șase principii non-negociabile.' : 'Six non-negotiable principles.'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {principlesEng.map((p, i) => (
              <motion.div
                key={p}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="relative rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md p-5 flex items-start gap-3 hover:border-primary/40 transition-all"
              >
                <Quote className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-base font-semibold leading-relaxed">{p}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RECOGNITION */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {ro ? 'Credibilitate' : 'Credibility'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
              {ro ? 'Probat în producție, validat în UE.' : 'Production-proven, EU-validated.'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {recognition.map((r, i) => (
              <motion.div
                key={r.t}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-6 text-center hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all"
              >
                <div className="h-12 w-12 mx-auto rounded-2xl bg-primary/15 text-primary grid place-items-center mb-3">
                  <r.icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-base mb-1">{r.t}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{r.d}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="shadow-xl shadow-primary/30">
              <Link to="/site/contact" className="gap-2">
                {ro ? 'Vorbește cu echipa' : 'Talk to the team'} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <SiteCtaBlock />
    </>
  );
};

export default SiteDespre;
